// ---------------------------------------------------------------------------
// TypesList — read-only table of creative types (platform defaults + tenant
// overrides merged). Feature #85, owned by OPUS-D17.
//
// Columns: code + label, MIME allowlist (rendered as chips), max size
// (human-readable), source (platform | tenant override). Per-row actions let
// the route open the edit modal or revert a tenant override back to the
// platform default.
//
// No writes happen here — the table surfaces callbacks that the page wires
// to mutations. Keeps this component cheap to re-render and easy to test.
// ---------------------------------------------------------------------------

import { memo, useMemo } from "react";
import type { CreativeTypeView } from "./api";
import { formatBytes, sortTypesByCode } from "./api";

// Source badge palette — light theme only, zinc borders, no orange / purple.
// "platform" uses zinc (neutral, inherited); "tenant" uses emerald (branded,
// owned by the current tenant).
const SOURCE_TINT = {
	platform: "bg-zinc-50 text-zinc-700 border-zinc-200",
	tenant: "bg-green-50 text-green-700 border-green-200",
} as const;

function SourceBadge({
	source,
	shadowsPlatform,
}: {
	source: "platform" | "tenant";
	shadowsPlatform?: boolean;
}) {
	const label = source === "platform" ? "Platform" : shadowsPlatform ? "Tenant override" : "Tenant";
	return (
 <span
 className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${SOURCE_TINT[source]}`}
 >
 {label}
 </span>
	);
}

function MimeChip({ mime }: { mime: string }) {
	const display = mime === "*/*" ? "any" : mime;
	return (
 <span
 className="inline-flex items-center rounded-md border border-green-200 bg-green-50 px-1.5 py-0.5 font-mono text-[11px] text-green-700"
 title={mime}
 >
 {display}
 </span>
	);
}

export type TypesListProps = {
	data: CreativeTypeView[];
	isLoading?: boolean;
	isError?: boolean;
	errorMessage?: string;
	onEdit?: (type: CreativeTypeView) => void;
	onRevert?: (type: CreativeTypeView) => void;
	revertingCode?: string | null;
};

function TypesListInner({
	data,
	isLoading = false,
	isError = false,
	errorMessage,
	onEdit,
	onRevert,
	revertingCode = null,
}: TypesListProps) {
	const rows = useMemo(() => sortTypesByCode(data ?? []), [data]);

	return (
 <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
 <table className="w-full border-collapse text-left text-sm">
 <thead className="bg-zinc-50">
 <tr className="border-b border-zinc-200 text-xs font-semibold uppercase tracking-wide text-zinc-500">
 <th className="px-4 py-3">Type</th>
 <th className="px-4 py-3">Allowed MIME</th>
 <th className="px-4 py-3">Max size</th>
 <th className="px-4 py-3">Source</th>
 <th className="px-4 py-3 text-right">Actions</th>
 </tr>
 </thead>
 <tbody>
 {isError ? (
 <tr>
 <td colSpan={5} className="px-4 py-10 text-center text-sm text-red-600">
 {errorMessage ?? "Failed to load creative types."}
 </td>
 </tr>
 ) : isLoading ? (
 <tr>
 <td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-500">
 Loading creative types…
 </td>
 </tr>
 ) : rows.length === 0 ? (
 <tr>
 <td colSpan={5} className="px-4 py-10 text-center text-sm text-zinc-500">
 No creative types defined yet.
 </td>
 </tr>
 ) : (
 rows.map((t) => (
 <tr
 key={t.code}
 className="border-b border-zinc-100 align-top last:border-0 hover:bg-zinc-50/60"
 >
 <td className="px-4 py-3">
 <div className="flex flex-col">
 <span className="font-mono text-xs text-zinc-500">{t.code}</span>
 <span className="text-sm font-medium text-zinc-900">{t.label}</span>
 </div>
 </td>
 <td className="px-4 py-3">
 <div className="flex flex-wrap gap-1">
 {t.allowed_mimes.length === 0 ? (
 <span className="text-xs text-zinc-400">—</span>
 ) : (
 t.allowed_mimes.map((m) => <MimeChip key={m} mime={m} />)
 )}
 </div>
 </td>
 <td className="px-4 py-3">
 <span className="text-sm text-zinc-700">
 {formatBytes(t.max_size_bytes)}
 </span>
 </td>
 <td className="px-4 py-3">
 <SourceBadge source={t.source} shadowsPlatform={t.shadows_platform} />
 </td>
 <td className="px-4 py-3 text-right">
 <div className="inline-flex items-center gap-2">
 {onEdit ? (
 <button
 type="button"
 onClick={() => onEdit(t)}
 className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 shadow-sm transition-colors hover:border-zinc-300 hover:bg-zinc-50"
 >
 {t.source === "tenant" ? "Edit" : "Override"}
 </button>
 ) : null}
 {onRevert && t.source === "tenant" ? (
 <button
 type="button"
 disabled={revertingCode === t.code}
 onClick={() => onRevert(t)}
 className="rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
 >
 {revertingCode === t.code ? "Reverting…" : "Revert"}
 </button>
 ) : null}
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
	);
}

export const TypesList = memo(TypesListInner);
TypesList.displayName = "TypesList";
